import React, { useState } from "react";
import { useRouteError } from "react-router-dom";
import { FlexColumn } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import TelegramId from "@/components/TelegramId/TelegramId";
import { parseErrorCode } from "@/utils/helpers";
import { CopyIcon } from "@/assets/icons";
import { openTelegramLink } from "@telegram-apps/sdk-react";
import { useAuth } from "@/store/authStore";
import { useTranslation } from "react-i18next";
import { useServiceType } from "@/utils/hooks/useServiceType";
import cls from "./styles.module.scss";

export default function ErrorFallback({ errorText }: { errorText?: string }) {
  const { t } = useTranslation();
  const error = useRouteError();
  const stack = error instanceof Error ? error.stack : null;
  const { initData } = useAuth.getState();
  const { isSravniDohod } = useServiceType();

  const rawMessage =
    errorText ?? (error instanceof Error ? error.message : String(error));

  const code = parseErrorCode(
    rawMessage,
    error instanceof Error ? error?.name : undefined
  );

  const [copied, setCopied] = useState(false);
  const [copiedForSupport, setCopiedForSupport] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(rawMessage || code || t("errorFallback.unknownError"))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
  };

  const handleSupportMessage = async () => {
    const message = [
      `${t("errorFallback.error")}: ${
        rawMessage || code || t("errorFallback.unknownError")
      }`,
      code && `${t("errorFallback.code")}: ${code}`,
      stack && `\n${t("errorFallback.stack")}:\n${stack}`,
      initData && `\nInitData:\n${initData}`,
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(message);
      setCopiedForSupport(true);
      setTimeout(() => setCopiedForSupport(false), 3000);

      if (openTelegramLink.isAvailable()) {
        openTelegramLink(`https://t.me/BigKefSupport`);
      } else {
        window.open(`https://t.me/BigKefSupport`, "_blank");
      }
    } catch (err) {
      console.error(`${t("errorFallback.errorCopying")}:`, err);
    }
  };

  return (
    <>
      <TelegramId color="#FFFFFF40" />
      <FlexColumn
        className={cls.wrapper}
        justifyContent="center"
        alignItems="center"
      >
        <Typography variant="h5">
          {t("errorFallback.somethingWentWrong")}
        </Typography>
        <Typography className={cls.desc}>
          {t("errorFallback.contactUs")}
          <br />
          <a
            href={`https://t.me/${
              isSravniDohod ? "SravniDohodSupport" : "BigKefSupport"
            }`}
            className={cls.link}
          >
            {isSravniDohod ? "@SravniDohodSupport" : "@BigKefSupport"}
          </a>
        </Typography>

        <div className={cls.messageRow}>
          <Typography style={{ color: "#FFFFFF40" }} variant="body3">
            {errorText || code}
          </Typography>
        </div>

        {copied && (
          <Typography className={cls.copiedToast} variant="body3">
            {t("errorFallback.copied")}
          </Typography>
        )}
        {copiedForSupport && (
          <Typography className={cls.copiedToast} variant="body3">
            {t("errorFallback.errorInfoCopied")}
          </Typography>
        )}

        {(stack || errorText || code) && (
          <div className={cls.details}>
            <button
              type="button"
              onClick={handleCopy}
              className={cls.copyButton}
            >
              <CopyIcon />
              {t("errorFallback.copyError")}
            </button>
            <summary className={cls.summary} />
            <pre className={cls.stack}>{stack || errorText || code}</pre>
            <button
              type="button"
              onClick={handleSupportMessage}
              className={cls.copyButton}
            >
              {t("errorFallback.shareError")}
            </button>
          </div>
        )}
      </FlexColumn>
    </>
  );
}
